import Paho from 'paho-mqtt';

import helpers from '../../utils/helpers';
import stringUtils from '../../utils/stringUtils';
import {createError} from '../errors/errorService';
import {log} from '../log';
import cmTimeouts from '../../consts/communicationLayer/cmTimeouts';
import {MW_ERRORS} from '../../consts/app/mwErrors';

const instances = {};

export default class CmClient {
    constructor(id, options, instanceName) {
        const instance = instances[instanceName];
        const isNewConnection = id && options;

        if (instance) {
            if (isNewConnection) {
                //disconnect previous
                instance.disconnect();
            } else {
                //return current initialized instance
                return instance;
            }
        } else if (!isNewConnection) {
            //no current instance is initialized and not a isNewConnection
            throw new Error("CM Client isn't initialized.");
        }

        //init new instance
        instances[instanceName] = this;
        this.instanceName = instanceName;
        this.id = id;
        this.isForceDisconnect = false;
        this.fillInOptions(options);
        this.initClient();

        return this;
    }

    fillInOptions(options) {
        const {endpoint, authToken, serialNumber, topicPublish, topicSubscribe} = options || {};

        this.endpoint = endpoint;
        this.token = authToken?.token;
        this.topicPublish = topicPublish;
        this.topicSubscribe = topicSubscribe;
        this.serialNumber = serialNumber;
    }

    initClient() {
        if (this.endpoint && this.token && this.topicPublish && this.serialNumber) {
            const path = stringUtils.formatString('/mqtt?{0}', this.token);
            const logPath = this.getPathToLog(path);
            const msg = `CmClient: start connection for id: ${this.id} with endpoint: ${this.endpoint} and date: ${logPath}`;

            log.info(msg);
            this.client = new Paho.Client(this.endpoint, 443, path, this.serialNumber);
        } else {
            const msg = `CM Client can't be initialized for id: ${this.id}.`;

            log.info(msg);
            throw new Error(msg);
        }
    }

    getPathToLog(path) {
        const keys = path.split('&');
        const createDate = keys?.find((x) => x?.startsWith('X-Amz-Date'));
        const expireDate = keys?.find((x) => x?.startsWith('X-Amz-Expires'));

        return createDate + ' ' + expireDate;
    }

    connect() {
        this.attachConnectHandler();
        this.attachCloseHandler();

        const connectOptions = {
            useSSL: true,
            onFailure: (e) => {
                log.info(`CM Client on connect errorCode: ${e?.errorCode} for id: ${this.id}.`);
                this.fillInOptions();
                this.reconnectClient(this.onError);
            },
        };

        this.client.connect(connectOptions);
    }

    async reconnectClient(onError) {
        try {
            this.isForceDisconnect = false;
            await helpers.timeout(cmTimeouts.CM_RECONNECT_TIMEOUT * 1000);
            this.checkToken();
            this.initClient();
            this.connect();
        } catch (e) {
            if (e.code === MW_ERRORS.ERROR_TOKEN_EXPIRED.code) {
                await onError();
            } else {
                await this.reconnectClient(onError);
            }
        }
    }

    isConnected(id) {
        if (this.id !== id) {
            return false;
        }
        return this.client && this.client.isConnected();
    }

    disconnect() {
        if (this.isConnected(this.id)) {
            this.client.disconnect();
        }
        this.isForceDisconnect = true;
    }

    disconnectAndClear() {
        this.disconnect();
        instances[this.instanceName] = undefined;
    }

    subscribe() {
        log.info(`CmClient: subscribe on topic: ${this.topicSubscribe}`);
        this.client.subscribe(this.topicSubscribe);
    }

    unsubscribe() {
        if (this.isConnected(this.id)) {
            log.info(`CmClient: unsubscribe from topic: ${this.topicSubscribe}`);
            this.client.unsubscribe(this.topicSubscribe);
        }
    }

    publish(message) {
        if (this.isConnected(this.id) && this.topicPublish) {
            let messageToLog = message;

            if (message.token) {
                // eslint-disable-next-line no-unused-vars
                const {token, ...rest} = message;

                messageToLog = rest;
            }

            log.info(
                `CmClient: publish message, topic: ${this.topicPublish}, message: ${JSON.stringify(messageToLog)})}`
            );
            this.client.publish(this.topicPublish, JSON.stringify(message));
        }
    }

    attachHandlers(onConnectHandler, onCloseHandler, onError) {
        this.onConnectHandler = onConnectHandler;
        this.onCloseHandler = onCloseHandler;
        this.onError = onError;
    }

    attachMessageHandler(onNewMessageHandler) {
        this.client.onMessageArrived = (message) => {
            if (this.isForceDisconnect) return;

            onNewMessageHandler(message);
        };
    }

    attachConnectHandler() {
        this.client.onConnected = () => {
            log.info(`CmClient: connected for id: ${this.id}.`);

            if (this.isForceDisconnect) {
                this.disconnect();
            } else {
                this.onConnectHandler();
            }
        };
    }

    attachCloseHandler() {
        this.client.onConnectionLost = async (message) => {
            log.info(`CmClient: connection lost for id: ${this.id}. ErrorCode: ${message.errorCode}`);
            this.onCloseHandler();

            if (this.isForceDisconnect) return;

            switch (message.errorCode) {
                case 0:
                    //forced close from code
                    break;
                case 5:
                    this.onError?.();
                    //unhandled error from paho. ignored
                    break;
                case 8:
                    this.onError?.();
                    //duplicate connection error. User is used another instance of cm
                    //redirect cant be used because error code 8 also happens when no internet connection (abnormal close)
                    break;
                default:
                    this.reconnectClient(this.onError);
                    break;
            }
        };
    }

    checkToken() {
        if (this.token) {
            const awsParams = this.token.split('&');
            const expireDateParam = awsParams.find((x) => x?.toLowerCase().includes('x-amz-expires='));
            const expireDateStr = expireDateParam.split('=')[1].toLowerCase();

            //parse only if correct date format
            if (expireDateStr.length === 16 && expireDateStr.includes('t') && expireDateStr.endsWith('z')) {
                const year = expireDateStr.substring(0, 4);
                const month = expireDateStr.substring(4, 6);
                const date = expireDateStr.substring(6, 8);
                const hours = expireDateStr.substring(9, 11);
                const min = expireDateStr.substring(11, 13);
                const sec = expireDateStr.substring(13, 15);
                const isoDate = `${year}-${month}-${date}T${hours}:${min}:${sec}.000Z`;
                const expireDate = new Date(isoDate);

                if (expireDate > new Date()) return;
            } else {
                log.info(`CmClient: Wrong Aws Token for id: ${this.id} expire date: ${expireDateStr}`);
            }
        }

        throw createError(MW_ERRORS.ERROR_TOKEN_EXPIRED, 'Token is expired');
    }
}
